import type { PlannerSkill, PlannerSkillIndexEntry } from "@amazing-wedding/shared-types";
import { parseSkillRaw } from "./parser";

/**
 * All skill markdown files bundled at build time. Vite inlines them so
 * runtime lookup is a synchronous in-memory map — no I/O, no Firestore.
 */
const skillModules = import.meta.glob("./skills/*.md", {
  query: "?raw",
  import: "default",
  eager: true,
}) as Record<string, string>;

const SKILLS = new Map<string, PlannerSkill>(
  Object.entries(skillModules).map(([path, raw]) => {
    const skill = parseSkillRaw(raw, path);
    const filename = path.split("/").pop()!.replace(/\.md$/, "");
    if (filename !== skill.frontmatter.id) {
      throw new Error(
        `Skill filename "${filename}.md" must match frontmatter id "${skill.frontmatter.id}".`,
      );
    }
    return [skill.frontmatter.id, skill];
  }),
);

/** All loaded skills, ordered by id for deterministic system-prompt output. */
const SKILL_LIST: PlannerSkill[] = [...SKILLS.values()].sort((a, b) =>
  a.frontmatter.id.localeCompare(b.frontmatter.id),
);

export function getSkillIndex(): PlannerSkillIndexEntry[] {
  return SKILL_LIST.map(({ frontmatter }) => ({
    id: frontmatter.id,
    title: frontmatter.title,
    whenToUse: frontmatter.whenToUse,
    tags: frontmatter.tags,
    requiresAttachment: frontmatter.requiresAttachment,
  }));
}

export function getSkill(id: string): PlannerSkill | null {
  return SKILLS.get(id) ?? null;
}

export function getSkillBody(id: string): string | null {
  return SKILLS.get(id)?.body ?? null;
}
